import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { PanAppState, SelectedCustomerState } from '@panamax/app-state';
import {
  MERCH_FEATURE,
  ProductAnalyticsService,
  ProductTrackingInfo,
} from '@usf/ngrx-product';
import { CustomerAlsoBoughtRecommendation } from '@usf/product-types';
import { Observable, combineLatest, of } from 'rxjs';
import { debounceTime, filter, map, take, tap } from 'rxjs/operators';
import { HomeService } from 'src/app/home/services/home.service';
import { PdpLnkSrc } from 'src/app/shared/constants/pdp-lnksrc.enum';
import { Product } from 'src/app/shared/models/product.model';
import { QuantityUpdate } from 'src/app/shared/models/quantity-update';
import { ProductService } from 'src/app/shared/services/product/product.service';
import { ProductDetailViewModel } from '../../models/product-detail-view-model';
import { ProductDetailTrackPageLoadService } from '../../services/product-detail-track-page-load.service';
import { ProductDetailService } from '../../services/product-detail.service';
import { ProductCabService } from '@product-detail/services/customers-also-bought.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { ProductDetailRelatedProducts } from '@product-detail/models/product-detail-related-products.model';
import Swiper from 'swiper';

@Component({
  selector: 'app-product-detail-related-products',
  templateUrl: './product-detail-related-products.component.html',
  styleUrls: ['./product-detail-related-products.component.scss'],
})
export class ProductDetailRelatedProductsComponent implements OnInit {
  readonly MERCH_FEATURE = MERCH_FEATURE;

  @Input() currentProductNumber: number;
  @Input() product: ProductDetailViewModel;
  @Input() isDisable = false;
  @Input() isGuestUser = false;
  @Output() productImageClickEmitter = new EventEmitter<Product>();

  isCabV2Enabled = false;

  selectedCustomer: SelectedCustomerState;
  productRecommendations$: Observable<ProductDetailRelatedProducts>;
  complementaryProducts$: Observable<Product[]>;
  customerAlsoBoughtIsTracked = false;
  mayAlsoNeedIsTracked = false;
  startSlice = 0;
  endSlice = 12;
  lnkSrc = {
    cabLnkSrc: PdpLnkSrc.customersAlsoBought,
    ymanLnkSrc: PdpLnkSrc.youMayAlsoNeed,
  };
  isPageEventLoaded = false;
  mapTypes = {};

  constructor(
    private productService: ProductService,
    private productDetailService: ProductDetailService,
    private homeService: HomeService,
    private productDetailTrackPageLoadService: ProductDetailTrackPageLoadService,
    private productCabService: ProductCabService,
    private panAppState: PanAppState,
    private productAnalyticsService: ProductAnalyticsService,
  ) {
    // Memory Leak
    this.homeService.loadSelectedCustomerState$()?.subscribe(cust => {
      this.selectedCustomer = cust;
    });
  }

  async ngOnInit() {
    this.isCabV2Enabled = !!(await this.panAppState.featureEnabled(
      FEATURES.split_global_cab_v2.name,
    ));
    this.loadCustomerRecommendations();
  }
  setSwiperInstance(swiper: Swiper, merchFeatureName: string) {
    swiper.params.watchSlidesProgress = true;
    swiper.on('transitionEnd', event => {
      const products = event.slides
        .filter(x => x.className.indexOf('swiper-slide-visible') !== -1)
        .map(x => {
          const attributes: any = x.children[0].attributes;
          return {
            divisionApn: attributes.divisionapn.value,
            attributes: attributes.attributes.value,
            merchFeatureView: '1',
            merchFeatureName: merchFeatureName,
          };
        });
      switch (merchFeatureName) {
        case MERCH_FEATURE.customerAlsoBoughtName:
          this.productAnalyticsService.trackCABScroll(products);
          break;
        case MERCH_FEATURE.youMayAlsoNeedName:
          this.productAnalyticsService.trackYMANScroll(products);
          break;
      }
    });
  }

  loadCustomerRecommendations = () => {
    if (this.product?.detail?.productNumber != this.product?.productNumber)
      return;

    if (!!this.isPageEventLoaded) return;

    this.loadYouMayAlsoNeed();
    this.loadCustomersAlsoBought();
    this.isPageEventLoaded = true;
  };

  private loadYouMayAlsoNeed() {
    const worksWellWithArray = ([] as number[]).concat(
      this.product?.summary?.worksWellWith?.complement ?? [],
      this.product?.summary?.worksWellWith?.productLine ?? [],
      this.product?.summary?.worksWellWith?.affinity ?? [],
    );
    const mayAlsoNeedProducts =
      worksWellWithArray.length >= 1 ? worksWellWithArray.filter(x => !!x) : [];

    if (worksWellWithArray?.length) {
      Object.keys(this.product?.summary?.worksWellWith).map(key => {
        this.product?.summary?.worksWellWith[key].forEach(
          product => (this.mapTypes[product] = key),
        );
      });
    }

    if (worksWellWithArray.length >= 1) {
      const mayAlsoNeedTrackingInfos: ProductTrackingInfo[] = [];

      this.complementaryProducts$ = this.productService
        .getYMANProducts(mayAlsoNeedProducts)
        .pipe(
          debounceTime(600),
          map(products => {
            if (this.isGuestUser) {
              return products?.filter(product =>
                ['0', '1'].includes(product?.inventory?.productStatus),
              );
            }
            return products;
          }),
          tap(products => {
            if (!this.mayAlsoNeedIsTracked) {
              const productsSlice = products?.slice(
                this.startSlice,
                this.endSlice,
              );
              productsSlice.forEach(p => {
                mayAlsoNeedTrackingInfos.push({
                  productNumber: p?.productNumber,
                  attributes: p?.trackingAttributes,
                  mapType: this.mapTypes[p?.productNumber],
                });
              });
              this.productDetailTrackPageLoadService.nextMayAlsoNeed(
                mayAlsoNeedTrackingInfos,
              );
              this.mayAlsoNeedIsTracked = true;
            }
          }),
        );
    } else {
      this.productDetailTrackPageLoadService.nextMayAlsoNeed([]);
    }
  }

  private loadCustomersAlsoBought() {
    let itemCab: CustomerAlsoBoughtRecommendation[];
    let pimCab: CustomerAlsoBoughtRecommendation[];
    if (this.selectedCustomer?.customerType === 'NA') {
      itemCab = this.product?.detail.nationalCABRecommendations ?? [];
      pimCab =
        this.product?.detail.pimCABRecommendation?.pimNationalRecommendations ??
        [];
    } else {
      itemCab = this.product?.detail.divisionCABRecommendations ?? [];
      pimCab =
        this.product?.detail.pimCABRecommendation?.pimDivisionRecommendations ??
        [];
    }

    const cabProducts = this.isCabV2Enabled
      ? this.productCabService.getCabProducts(this.product)
      : !!itemCab?.length || !!pimCab?.length
        ? this.getCABProducts(itemCab, pimCab).pipe(debounceTime(600), take(1))
        : of({} as ProductDetailRelatedProducts);

    const customerAlsoBoughtTrackingInfos: ProductTrackingInfo[] = [];
    this.productRecommendations$ = combineLatest([
      cabProducts,
      this.productDetailTrackPageLoadService?.mayAlsoNeed$,
    ]).pipe(
      filter(([_, ymanProducts]) => !!ymanProducts),
      map(([cabProducts, ymanProducts]) => {
        const ymanProductNumbers = new Set(
          ymanProducts?.map(ymanProduct => ymanProduct?.productNumber),
        );

        const products = cabProducts?.products?.filter(
          product =>
            !ymanProductNumbers.has(product?.productNumber) &&
            (!this.isGuestUser ||
              ['0', '1'].includes(product?.inventory?.productStatus)),
        );

        return {
          products,
          variant: cabProducts?.variant,
        };
      }),
      tap(cabProduct => {
        if (!this.customerAlsoBoughtIsTracked) {
          const productsSlice = cabProduct?.products?.slice(
            this.startSlice,
            this.endSlice,
          );
          productsSlice?.forEach(p => {
            customerAlsoBoughtTrackingInfos.push({
              productNumber: p?.productNumber,
              attributes: p?.trackingAttributes,
              variant: cabProduct?.variant,
            });
          });
          this.productDetailTrackPageLoadService.nextCustomerAlsoBought(
            customerAlsoBoughtTrackingInfos,
          );
          this.customerAlsoBoughtIsTracked = true;
        }
      }),
    );
  }

  onQuantityUpdate(
    quantityUpdate: QuantityUpdate,
    merch: string,
    rank: number,
    variant?: string,
  ) {
    this.productService
      .getProduct(quantityUpdate.productNumber)
      .pipe(take(1))
      .subscribe(product => {
        this.productDetailService.trackRelatedProductChangeQuantity(
          quantityUpdate,
          product,
          merch,
          rank,
          null,
          this.mapTypes[product?.productNumber],
          variant,
        );
      });
  }

  getCABProducts(
    itemRecommendations: CustomerAlsoBoughtRecommendation[],
    pimRecommendations: CustomerAlsoBoughtRecommendation[],
  ): Observable<ProductDetailRelatedProducts> {
    const itemRecs = itemRecommendations
      .filter(rec => !!rec.originalProductNumber)
      .sort(this.rankSort);

    const pimRecs = [...pimRecommendations].sort(this.rankSort);

    const seen = new Set<number>();
    const allProductNums = [...itemRecs, ...pimRecs]
      .map(rec => rec.recommendedProductNumber)
      .filter(productNumber =>
        seen.has(productNumber) || productNumber === this.currentProductNumber
          ? false
          : seen.add(productNumber),
      );
    if (allProductNums.length === 0)
      return of({
        products: [],
        variant: 'old',
      });
    return this.productService.loadCABProducts(allProductNums).pipe(
      map(productMap => {
        const itemRecProducts: Product[] = [];
        allProductNums.forEach(rec => {
          const product = productMap.get(rec);
          if (!!product) itemRecProducts.push(product);
        });
        return {
          products: [...itemRecProducts],
          variant: 'old',
        };
      }),
    );
  }

  rankSort(a, b) {
    return a.rank - b.rank;
  }

  onProductImageClick(product: Product, rank: number) {
    this.productImageClickEmitter.emit({ ...product, resultRank: rank });
  }
}
